import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook สำหรับเตือนผู้ใช้เมื่อมีข้อมูลที่ยังไม่ได้บันทึก
 * - แสดง native dialog เมื่อปิดแท็บ/รีเฟรช (browser requirement — customize ไม่ได้)
 * - แสดง custom ConfirmModal เมื่อ navigate ภายใน app:
 *   1. Browser back/forward → intercept ด้วย popstate
 *   2. Sidebar/Link clicks → intercept ด้วย click event listener บน <a> tags
 *
 * Note: ไม่ใช้ useBlocker เพราะต้องการ data router (createBrowserRouter)
 *       แต่ project ใช้ BrowserRouter ซึ่งไม่ support useBlocker
 */
export function useUnsavedChanges(isDirty: boolean, _message?: string) {
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const [showModal, setShowModal] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);
  const navigate = useNavigate();

  // Browser tab close/refresh warning (native dialog — unavoidable)
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Browser back/forward navigation blocking via popstate
  useEffect(() => {
    if (!isDirty) return;

    // Push a dummy state so we can intercept back navigation
    window.history.pushState({ unsavedChangesGuard: true }, '');

    const handlePopState = () => {
      if (isDirtyRef.current) {
        // Re-push state to prevent navigation and show modal
        window.history.pushState({ unsavedChangesGuard: true }, '');
        pendingNavigationRef.current = '__BACK__';
        setShowModal(true);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty]);

  // Intercept sidebar/link clicks within the app
  useEffect(() => {
    if (!isDirty) return;

    const handleClick = (e: MouseEvent) => {
      // Find closest <a> element
      const anchor = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Only intercept internal navigation (starts with / and not external links)
      if (!href.startsWith('/')) return;

      // Don't intercept links that open in new tab (e.g. help PDF)
      if (anchor.target === '_blank') return;

      // Don't intercept if navigating to current page
      if (href === window.location.pathname) return;

      // Block navigation and show modal
      if (isDirtyRef.current) {
        e.preventDefault();
        e.stopPropagation();
        pendingNavigationRef.current = href;
        setShowModal(true);
      }
    };

    // Use capture phase to intercept before React Router handles the click
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [isDirty]);

  const onConfirmLeave = useCallback(() => {
    setShowModal(false);
    isDirtyRef.current = false;

    const target = pendingNavigationRef.current;
    pendingNavigationRef.current = null;

    if (target === '__BACK__') {
      window.history.go(-2); // Go back past the guard state
    } else if (target) {
      navigate(target);
    }
  }, [navigate]);

  const onCancelLeave = useCallback(() => {
    setShowModal(false);
    pendingNavigationRef.current = null;
  }, []);

  return { showModal, onConfirmLeave, onCancelLeave };
}
